import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Link, Copy, ExternalLink, Info } from 'lucide-react';
import { useToast } from '@/components/ui/use-toast';
import { apiClient } from '@/lib/api';
import copyToClipboard from 'copy-to-clipboard';

interface ApiKey {
    id: string;
    name: string;
    unkey: string;
    created_at: string;
}

interface RemoteMCPProps {
    apiKey?: string; // Optional API key parameter
    showSelector?: boolean; // Whether to show the API key selector
    className?: string;
}

export function RemoteMCP({
    apiKey: providedApiKey,
    showSelector = true,
    className = ""
}: RemoteMCPProps) {
    const [apiKeys, setApiKeys] = useState<ApiKey[]>([]);
    const [selectedApiKey, setSelectedApiKey] = useState<string>('');
    const [loading, setLoading] = useState(true);
    const [generatedUrl, setGeneratedUrl] = useState<string>('');
    const { toast } = useToast();

    useEffect(() => {
        if (providedApiKey) {
            // If API key is provided as prop, use it directly
            setLoading(false);
            setSelectedApiKey('provided');
        } else if (showSelector) {
            // Only load API keys if we need to show the selector
            loadApiKeys();
        } else {
            setLoading(false);
        }
    }, [providedApiKey, showSelector]);

    const loadApiKeys = async () => {
        try {
            setLoading(true);
            const response = await apiClient.getApiKeys();
            setApiKeys(response.apiKeys || []);

            // Auto-select the first API key if available
            if (response.apiKeys && response.apiKeys.length > 0) {
                setSelectedApiKey(response.apiKeys[0].id);
            }
        } catch (error) {
            console.error('Failed to load API keys:', error);
            toast({
                title: "Error",
                description: "Failed to load API keys. Please try again.",
                variant: "destructive",
            });
        } finally {
            setLoading(false);
        }
    };

    const getSelectedApiKeyValue = (): string | null => {
        if (providedApiKey) {
            return providedApiKey;
        }

        const selectedKey = apiKeys.find(key => key.id === selectedApiKey);
        return selectedKey?.unkey || null;
    };

    const handleCopyToClipboard = (text: string, successMessage: string = "Copied to clipboard!") => {
        const success = copyToClipboard(text);

        if (success) {
            toast({
                title: "Success",
                description: successMessage,
            });
        } else {
            toast({
                title: "Error",
                description: "Failed to copy to clipboard. Please copy manually.",
                variant: "destructive",
            });
        }
    };

    const generateMCPLink = () => {
        const apiKeyValue = getSelectedApiKeyValue();

        if (!apiKeyValue) {
            toast({
                title: "Error",
                description: "Please select an API key first.",
                variant: "destructive",
            });
            return;
        }

        const mcpUrl = `https://mcp.plusefin.com/mcp?apikey=${apiKeyValue}`;
        setGeneratedUrl(mcpUrl);

        // Use the copy-to-clipboard package
        handleCopyToClipboard(mcpUrl, "MCP connection URL generated and copied to clipboard!");
    };

    const handleCopyUrl = () => {
        if (generatedUrl) {
            handleCopyToClipboard(generatedUrl, "MCP URL copied to clipboard!");
        }
    };

    const openInNewTab = () => {
        if (generatedUrl) {
            window.open(generatedUrl, '_blank');
        }
    };

    const canGenerateLink = providedApiKey || selectedApiKey;

    return (
        <Card className={`w-full ${className}`}>
            <CardHeader>
                <CardTitle className="flex items-center gap-2">
                    <Link className="w-5 h-5" />
                    Remote MCP
                </CardTitle>
                <CardDescription>
                    Connect directly to PlusE Fin's remote MCP server for a seamless experience without local installation or configuration.
                    Click the button below to generate the MCP connection URL.
                    For examples on how to use the remote MCP, click{' '}
                    <a href="https://plusefin.com/docs/mcp-client-setup/" className="text-blue-600 hover:underline">here</a>.
                </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
                {showSelector && !providedApiKey && (
                    <div className="space-y-2">
                        <div className="flex items-center gap-2">
                            <label htmlFor="api-key-select" className="text-sm font-medium">
                                API Key
                            </label>
                            <Tooltip>
                                <TooltipTrigger asChild>
                                    <Info className="w-4 h-4 text-gray-800 cursor-help" />
                                </TooltipTrigger>
                                <TooltipContent>
                                    <p>API key to be used when connecting your MCP client</p>
                                </TooltipContent>
                            </Tooltip>
                        </div>

                        <Select
                            value={selectedApiKey}
                            onValueChange={setSelectedApiKey}
                            disabled={loading || apiKeys.length === 0}
                        >
                            <SelectTrigger className="w-full">
                                <SelectValue placeholder={loading ? "Loading..." : "Select an API key"} />
                            </SelectTrigger>
                            <SelectContent>
                                {apiKeys.map((apiKey) => (
                                    <SelectItem key={apiKey.id} value={apiKey.id}>
                                        {apiKey.name} ({apiKey.unkey.substring(0, 8)}...)
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                )}

                {providedApiKey && (
                    <div className="space-y-2">
                        <label className="text-sm font-medium">Using API Key:</label>
                        <div className="p-2 bg-gray-50 rounded-md border text-sm font-mono">
                            {providedApiKey.substring(0, 8)}...{providedApiKey.substring(providedApiKey.length - 4)}
                        </div>
                    </div>
                )}

                <Button
                    onClick={generateMCPLink}
                    disabled={!canGenerateLink || loading}
                    className="w-full"
                >
                    <Link className="w-4 h-4 mr-2" />
                    Generate MCP Link
                </Button>

                {generatedUrl && (
                    <div className="space-y-2">
                        <label className="text-sm font-medium">Generated MCP URL:</label>
                        <div className="flex items-center gap-2 p-3 bg-gray-50 rounded-md border">
                            <code className="flex-1 text-sm break-all">
                                {generatedUrl}
                            </code>
                            <div className="flex gap-1">
                                <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={handleCopyUrl}
                                    className="h-8 w-8 p-0"
                                >
                                    <Copy className="w-4 h-4" />
                                </Button>
                                <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={openInNewTab}
                                    className="h-8 w-8 p-0"
                                >
                                    <ExternalLink className="w-4 h-4" />
                                </Button>
                            </div>
                        </div>
                        <p className="text-xs text-gray-600">
                            Use this URL in your MCP client to connect to the remote server.
                        </p>
                    </div>
                )}

                {showSelector && !providedApiKey && apiKeys.length === 0 && !loading && (
                    <div className="text-center py-4">
                        <p className="text-gray-600 mb-2">No API keys found.</p>
                        <Button variant="outline" asChild>
                            <a href="/dashboard/billing">Get API Key</a>
                        </Button>
                    </div>
                )}
            </CardContent>
        </Card>
    );
}

export default RemoteMCP;